import type { BattleView, CardDefinition, CardId, PlayCardCommand, WorldPoint } from '../../engine/types';
import { SeededRng } from '../../engine/rng';
import { resolveCardPlacement } from '../../engine/placement';
import type { BotPolicy, BotPolicyContext } from './types';

export interface BotCandidate {
  readonly cardId: CardId;
  readonly position: WorldPoint;
  readonly score: number;
  readonly reason: string;
}

export type CardScoreModifier = (card: CardDefinition, view: BattleView) => number;

const compactEntityHash = (view: BattleView): string => {
  let value = 0x811c9dc5;
  for (const entity of view.entities) {
    for (const part of [entity.id, Math.round(entity.x / 50), Math.round(entity.y / 50), entity.hp]) {
      value ^= part;
      value = Math.imul(value, 0x01000193);
    }
  }
  return (value >>> 0).toString(16).padStart(8, '0');
};

export class RoleBasedBotPolicy implements BotPolicy {
  private readonly rng: SeededRng;
  private readonly handEnteredAt = new Map<CardId, number>();
  private nextDecisionTick = 0;

  constructor(
    readonly id: string,
    readonly version: string,
    private readonly context: BotPolicyContext,
    private readonly scoreModifier: CardScoreModifier = () => 0,
  ) {
    this.rng = new SeededRng(context.seed ^ 0xa5a5a5a5);
  }

  decide(view: BattleView): PlayCardCommand | null {
    if (view.result || view.phase === 'countdown' || view.tick < view.player.nextCardReadyTick || view.tick < this.nextDecisionTick) return null;
    this.nextDecisionTick = view.tick + this.rng.integer(11, 24);
    const currentHand = new Set(view.player.hand);
    for (const cardId of currentHand) if (!this.handEnteredAt.has(cardId)) this.handEnteredAt.set(cardId, view.tick);
    for (const cardId of this.handEnteredAt.keys()) if (!currentHand.has(cardId)) this.handEnteredAt.delete(cardId);
    const candidates = this.buildCandidates(view)
      .map((candidate) => ({ ...candidate, score: candidate.score + this.scoreModifier(this.context.content.cards.get(candidate.cardId)!, view) }))
      .sort((left, right) => right.score - left.score || left.cardId.localeCompare(right.cardId));
    const selected = candidates[0];
    const visibleEntities = selected ? view.entities.map((entity) => ({ id: entity.id, owner: entity.owner, kind: entity.kind, cardId: entity.cardId, x: entity.x, y: entity.y, hp: entity.hp, radius: entity.radius, movement: entity.movement })) : undefined;
    this.context.journal.record({
      tick: view.tick,
      phase: 'bot',
      source: this.id,
      code: 'BOT_DECISION',
      cardIds: selected ? [selected.cardId] : [],
      payload: {
        botId: this.context.strategy.botId,
        botVersion: this.context.strategy.botVersion,
        policyId: this.id,
        policyVersion: this.version,
        strategySnapshotHash: this.context.strategy.modelHash,
        input: {
          elixirMilli: view.player.elixirMilli,
          hand: view.player.hand,
          activeDeploymentZoneIds: view.deploymentZones.map((zone) => zone.id),
          visibleEntityCount: view.entities.length,
          visibleEntityHash: compactEntityHash(view),
          ...(visibleEntities ? { visibleEntities } : {}),
        },
        candidates,
        selected: selected ?? null,
        reason: selected ? selected.reason : 'no-valid-candidate',
      },
    });
    if (!selected || selected.score < 0) return null;
    return { type: 'play-card', tick: view.tick, sequence: view.player.nextSequence, playerId: this.context.playerId, cardId: selected.cardId, position: selected.position };
  }

  private buildCandidates(view: BattleView): BotCandidate[] {
    const { worldBounds, riverTop, riverBottom, bridges } = view.arena;
    const centerY = worldBounds.y + worldBounds.height / 2;
    const threatBoundary = this.context.playerId === 'bot' ? centerY + (riverBottom - riverTop) / 2 : centerY - (riverBottom - riverTop) / 2;
    const affordable = view.player.hand.map((cardId) => this.context.content.cards.get(cardId)).filter((card): card is CardDefinition => Boolean(card && card.cost * 1_000 <= view.player.elixirMilli));
    const threats = view.entities.filter((entity) => entity.owner !== this.context.playerId && entity.kind !== 'tower' && !entity.dead && (this.context.playerId === 'bot' ? entity.y < threatBoundary : entity.y > threatBoundary));
    const friendlyTank = view.entities.find((entity) => entity.owner === this.context.playerId && !entity.dead && this.context.content.cards.get(entity.cardId)?.roles.includes('tank'));
    const ownDeployY = this.context.playerId === 'bot'
      ? Math.max(worldBounds.y + 900, view.arena.botDeployMaxY - 1_100)
      : Math.min(worldBounds.y + worldBounds.height - 900, view.arena.playerDeployMinY + 1_100);
    const defensiveY = ownDeployY;
    const result: BotCandidate[] = [];
    for (const card of affordable) {
      const spellMechanic = card.mechanics.find((mechanic) => mechanic.type === 'area-projectile');
      if (spellMechanic?.type === 'area-projectile') {
        const spell = this.context.content.spells.get(spellMechanic.spellId);
        if (spell?.behavior?.type === 'spawn-units') {
          const tower = view.entities
            .filter((entity) => entity.owner !== this.context.playerId && entity.kind === 'tower' && !entity.dead)
            .sort((left, right) => left.hp / left.maxHp - right.hp / right.maxHp || left.id - right.id)[0];
          if (tower) result.push({ cardId: card.id, position: { x: tower.x, y: tower.y }, score: 62 - card.cost * 2 + this.cardAgeBonus(card.id, view.tick), reason: 'projectile-spawn-on-tower' });
          continue;
        }
        const radius = spell?.radius ?? 1_000;
        const clusters = threats.map((anchor) => ({ anchor, count: threats.filter((other) => Math.hypot(anchor.x - other.x, anchor.y - other.y) <= radius).length }));
        const best = clusters.sort((left, right) => right.count - left.count || left.anchor.id - right.anchor.id)[0];
        if (best) {
          const targetRoles = this.context.content.cards.get(best.anchor.cardId)?.roles ?? [];
          const score = best.count * 34 - card.cost * 4 + (targetRoles.includes('tank') ? 20 : 0) + this.cardAgeBonus(card.id, view.tick);
          result.push({ cardId: card.id, position: { x: best.anchor.x, y: best.anchor.y }, score, reason: `area-value-${best.count}` });
        }
        continue;
      }
      if (threats.length) {
        const nearest = threats.slice().sort((left, right) => Math.abs(left.y - defensiveY) - Math.abs(right.y - defensiveY) || left.id - right.id)[0]!;
        let score = 45 - card.cost * 3 + this.cardAgeBonus(card.id, view.tick);
        const threatRoles = this.context.content.cards.get(nearest.cardId)?.roles ?? [];
        if (card.roles.includes('defense') && threatRoles.includes('tank')) score += 45;
        if (card.roles.includes('anti-air') && nearest.movement === 'air') score += 35;
        result.push({ cardId: card.id, position: { x: nearest.x, y: defensiveY }, score, reason: 'defend-nearest-threat' });
      } else if (friendlyTank && !card.roles.includes('tank') && !card.roles.includes('defense')) {
        const supportY = this.context.playerId === 'bot'
          ? Math.min(view.arena.botDeployMaxY, Math.max(worldBounds.y + 900, friendlyTank.y - 900))
          : Math.max(view.arena.playerDeployMinY, Math.min(worldBounds.y + worldBounds.height - 900, friendlyTank.y + 900));
        const roleScore = (card.roles.includes('splash') ? 12 : 0) + (card.roles.includes('anti-air') ? 8 : 0) + (card.roles.includes('ranged') ? 6 : 0);
        result.push({ cardId: card.id, position: { x: friendlyTank.x, y: supportY }, score: 45 + roleScore - card.cost + this.cardAgeBonus(card.id, view.tick), reason: 'support-tank' });
      } else if (view.player.elixirMilli >= 6_500) {
        const pockets = view.deploymentZones.filter((zone) => zone.unlockTowerSocketId);
        const pocket = pockets.length ? this.rng.pick(pockets) : null;
        const laneX = pocket ? Math.round(pocket.bounds.x + pocket.bounds.width / 2) : this.rng.pick(bridges.map((bridge) => bridge.x));
        const deployY = pocket ? Math.round(pocket.bounds.y + pocket.bounds.height / 2) : ownDeployY;
        const roleScore = card.roles.includes('tank')
          ? 75
          : 28 + (card.roles.includes('ranged') ? 12 : 0) + (card.roles.includes('splash') ? 12 : 0) + (card.roles.includes('anti-air') ? 8 : 0) - (card.roles.includes('defense') ? 24 : 0);
        result.push({ cardId: card.id, position: { x: laneX, y: deployY }, score: roleScore - card.cost + this.cardAgeBonus(card.id, view.tick), reason: pocket ? `attack-from-${pocket.id}` : 'start-offensive-push' });
      }
    }
    return result.flatMap((candidate) => {
      const card = this.context.content.cards.get(candidate.cardId);
      if (!card) return [];
      const position = this.findLegalPosition(view, card, candidate.position);
      return position ? [{ ...candidate, position }] : [];
    });
  }

  private cardAgeBonus(cardId: CardId, tick: number): number {
    const enteredAt = this.handEnteredAt.get(cardId) ?? tick;
    return Math.min(18, Math.floor(Math.max(0, tick - enteredAt) / 120));
  }

  private findLegalPosition(view: BattleView, card: CardDefinition, desired: WorldPoint): WorldPoint | null {
    const { worldBounds } = view.arena;
    const step = Math.max(500, Math.round(worldBounds.width / 12));
    const ownDirection = this.context.playerId === 'bot' ? -1 : 1;
    const offsets = [[0, 0], [-1, 0], [1, 0], [0, ownDirection], [-1, ownDirection], [1, ownDirection], [-2, 0], [2, 0]] as const;
    for (const [offsetX, offsetY] of offsets) {
      const position = {
        x: Math.max(worldBounds.x + 400, Math.min(worldBounds.x + worldBounds.width - 400, desired.x + offsetX * step)),
        y: Math.max(worldBounds.y + 400, Math.min(worldBounds.y + worldBounds.height - 400, desired.y + offsetY * step)),
      };
      const entities = view.entities.map((entity) => ({ kind: entity.kind, dead: entity.dead, removed: false, position: { x: entity.x, y: entity.y }, radius: entity.radius, arenaSocketId: null }));
      if (resolveCardPlacement({ playerId: this.context.playerId, card, position, content: this.context.content, arena: view.arena, entities, activeZones: view.deploymentZones }).valid) return position;
    }
    return null;
  }
}
